import { useQsFilters } from "@dash/filters";
import { Button } from "@dash/form";
import { useDeviceType } from "@dash/mq";
import { Page, PageHeader } from "@dash/page";
import { SearchWithPaginationSticky } from "@dash/search";
import { QuerySkeleton } from "@dash/skeleton";
import { MdAdd } from "react-icons/md";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom-v5-compat";

import { orderStatuses } from "@megaron/crm-contracts";
import { orderSortFields } from "@megaron/docs-contracts";
import { useClientManager } from "@megaron/react-clients";
import { getMonths, Serializers, SerializerValue } from "@megaron/serializers";
import { sleep } from "@megaron/utils";

import { EdiAlertsContainer } from "./ediAlerts";
import { parseStatusToDisplayData } from "./helpers";
import { OrdersFilters } from "./OrdersFilters";
import { OrdersList } from "./OrdersList";
import { OrdersTable } from "./OrdersTable";

const pageSize = 30;

const filterSerializer = Serializers.object({
  page: Serializers.integer,
  searchText: Serializers.string,
  regions: Serializers.array(Serializers.string).optional(),
  status: Serializers.stringOneOf(...orderStatuses)
    .array()
    .optional(),
  sort: Serializers.sortFilter(orderSortFields).asJsonString().optional(),
  type: Serializers.stringOneOf("order", "creditMemo").array().asJsonString().optional(),
  startDate: Serializers.month.optional(),
  endDate: Serializers.month.optional(),
});

export type Filters = SerializerValue<typeof filterSerializer>;

export const OrdersPage = () => {
  const { isMobile } = useDeviceType();
  const navigate = useNavigate();
  const { filters, setFilter, setFilters } = useQsFilters(filterSerializer, {
    page: 0,
    searchText: "",
    regions: undefined,
    status: undefined,
    sort: undefined,
    type: undefined,
    startDate: undefined,
    endDate: undefined,
  });

  const ordersQuery = useClientManager("docs")
    .searchOrders()
    .useQuery({
      offset: filters.page * pageSize,
      limit: pageSize,
      searchText: filters.searchText,
      status: filters.status,
      type: filters.type,
      region: filters.regions,
      month: filters.startDate && filters.endDate ? getMonths(filters.startDate, filters.endDate) : undefined,
      sort: filters.sort,
    });

  const ordersQueryCsv = useClientManager("docs")
    .searchOrders()
    .useQuery({
      offset: 0,
      limit: 10000,
      searchText: filters.searchText,
      status: filters.status,
      type: filters.type,
      region: filters.regions,
      month: filters.startDate && filters.endDate ? getMonths(filters.startDate, filters.endDate) : undefined,
      sort: filters.sort,
    });

  const queryClient = useQueryClient();

  const exportToCsv = (orders: NonNullable<typeof ordersQueryCsv.data>["items"]) => {
    if (!orders || !orders.length) return;

    const headers = [
      "Nr",
      "Data",
      "Numer",
      "Nabywca",
      "Odbiorca faktury",
      "Status",
      "Marża",
      "Wartość netto",
      "Region",
    ];

    const csvContent = [
      headers.join(";"),
      ...orders.map((order, index) =>
        [
          index + 1,
          order.orderDate,
          order.name,
          order.sellToName,
          order.billToName,
          parseStatusToDisplayData(order.status).label,
          String(order.profit).replace(".", ","),
          String(order.netTotal).replace(".", ","),
          order.region,
        ]
          .map((val) => `"${val ?? ""}"`)
          .join(";"),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "zamowienia.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Page
      css={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        maxWidth: "100%",
      }}
    >
      <PageHeader
        actions={
          <div css={{ display: "flex", gap: "12px" }}>
            <Button icon={<MdAdd />} onClick={() => navigate("/crm/order-drafts/new")}>
              Dodaj
            </Button>
            <Button onClick={() => exportToCsv(ordersQueryCsv.data?.items || [])} variant="outline">
              Eksportuj CSV
            </Button>
          </div>
        }
      >
        Zamówienia
      </PageHeader>

      <SearchWithPaginationSticky
        onSearchChange={setFilter("searchText")}
        searchValue={filters.searchText}
        page={filters.page}
        onPageChange={setFilter("page")}
        pageSize={pageSize}
        itemCount={ordersQuery.data?.count}
      ></SearchWithPaginationSticky>

      <OrdersFilters
        filters={filters}
        handlers={{
          onRegionsChange: setFilter("regions"),
          onStatusChange: setFilter("status"),
          onTypeChange: setFilter("type"),
          onSortChange: setFilter("sort"),
          onDatesChange: (startDate, endDate) => setFilters({ ...filters, startDate, endDate }),
        }}
      />

      <EdiAlertsContainer
        onEdiChanged={() => sleep(1000).then(() => queryClient.invalidateQueries(ordersQuery.key))}
        isVertical
      />

      <QuerySkeleton query={ordersQuery}>
        {(orders) =>
          isMobile ? (
            <OrdersList orders={orders.items} />
          ) : (
            <OrdersTable
              isLoading={false}
              orders={orders.items}
              activeSortValue={filters.sort}
              onSortChange={setFilter("sort")}
            />
          )
        }
      </QuerySkeleton>
    </Page>
  );
};
